import React, { ReactNode, useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Box } from '@wix/design-system';
import {
  GenericEventsBiLoggerProvider,
  PageContextProvider,
} from '@wix/data-tools-generic-events';
import { AppContextProvider } from '../AppLoader';
import { AuthDivider, AuthPage, BodyContainer } from '../AuthPage/AuthPage';
import {
  BEHAVIORS,
  ButtonWithLoader,
} from '../ButtonWithLoader/ButtonWithLoader';
import { DialogFormContainer } from '../DialogContainer';
import { DialogHeader } from '../DialogHeader/DialogHeader';
import { InputFieldWithLabel } from '../InputField';
import { ThemedText, ThemedTextButton } from '../ThemedComponents';
import { dataHooks } from '../../dataHooks';
import type { EmailCodeStore } from '../../stores/emailCodeStore';
import s from './EmailCodeDialog.scss';

/** Seconds until `availableAt` (epoch ms), ticking once a second. */
const useSecondsUntil = (availableAt: number) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    setNow(Date.now());
    if (availableAt <= Date.now()) {
      return undefined;
    }
    const interval = setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= availableAt) {
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [availableAt]);
  return Math.max(0, Math.ceil((availableAt - now) / 1000));
};

const ResendCode: React.FC<{ store: EmailCodeStore }> = observer(
  ({ store }) => {
    const { t } = useTranslation();
    const secondsLeft = useSecondsUntil(store.resendAvailableAt);
    if (secondsLeft > 0) {
      return (
        <ThemedText
          size="small"
          secondary
          dataHook={dataHooks.enterCode.resendButton}
        >
          {t('signup.email_code.resend_in', {
            defaultValue: 'Resend Code in {seconds}s',
            seconds: secondsLeft,
          })}
        </ThemedText>
      );
    }
    return (
      <Box>
        <ThemedTextButton
          size="small"
          underline="always"
          disabled={store.isLoading}
          onClick={() => store.resendCode()}
          dataHook={dataHooks.enterCode.resendButton}
        >
          {t('signup.email_code.resend', { defaultValue: 'Resend Code' })}
        </ThemedTextButton>
      </Box>
    );
  },
);

/** The "Check your email" step shared by email-code signup and login. */
export const EmailCodeDialog: React.FC<{
  store: EmailCodeStore;
  pageName: string;
  /** Prefix for the screen's own data hooks, e.g. `signup.emailCode`. */
  dataHookPrefix: string;
  submitLabel: string;
  /** The button that switches to the password form. */
  passwordLabel: string;
  /** A CAPTCHA a refused send asked for, under the resend link. */
  captcha?: ReactNode;
  /** Policies under the card, next to the reCAPTCHA notice that is always there. */
  belowCard?: ReactNode;
  /** Rendered at the bottom of the card, e.g. the policies. */
  children?: ReactNode;
}> = observer((props) => {
  const {
    store,
    pageName,
    dataHookPrefix,
    submitLabel,
    passwordLabel,
    captcha,
    belowCard,
    children,
  } = props;
  const { t } = useTranslation();
  const {
    rootStore: {
      displayStore: { isMobile, isVerticalLayout, isDialogCentered },
      biLogger,
      notificationStore,
    },
  } = useContext(AppContextProvider);

  useEffect(() => {
    notificationStore.close();
  }, []);

  const submit = () => store.verify({ code: store.codeField.value });

  return (
    <GenericEventsBiLoggerProvider value={biLogger}>
      <PageContextProvider pageContext={{ pageName }}>
        <Box
          direction="vertical"
          width="100%"
          align="center"
          className={isDialogCentered ? 'dialogCenteredWrapper' : undefined}
        >
          <DialogFormContainer
            dataHook={dataHooks.enterCode.container}
            formClasses={[s.form]}
            autoCenter={false}
            mainHeaderProps={{
              showBackButton: true,
              backButtonCallback: store.backToEmail,
            }}
          >
            <BodyContainer
              priority="secondary"
              withPolicies={Boolean(children)}
            >
              <DialogHeader>
                <DialogHeader.Title as="h1" flow="secondaryFlow">
                  {t('signup.email_code.title', {
                    defaultValue: 'Check your email',
                  })}
                </DialogHeader.Title>
                {/* One header child, so the email sits right under the line. */}
                <Box direction="vertical" align="center">
                  <DialogHeader.SubTitleSmall>
                    {t('signup.email_code.description', {
                      defaultValue: 'Enter the code sent to:',
                    })}
                  </DialogHeader.SubTitleSmall>
                  <DialogHeader.SubTitleSmall>
                    <Box gap={1} align="center" verticalAlign="middle">
                      <ThemedText weight="bold">{store.email}</ThemedText>
                      <ThemedTextButton
                        skin="standard"
                        underline="none"
                        onClick={store.backToEmail}
                        dataHook={`${dataHookPrefix}.changeEmail`}
                      >
                        {t('signup.email_code.change_email', {
                          defaultValue: 'Change',
                        })}
                      </ThemedTextButton>
                    </Box>
                  </DialogHeader.SubTitleSmall>
                </Box>
              </DialogHeader>
              <Box
                direction="vertical"
                align={isVerticalLayout ? 'center' : 'left'}
                className={s.codeInput}
                gap={4}
              >
                <Box direction="vertical" width="100%" gap={1}>
                  {/* No gap above resend, like Forgot Password under the login password. */}
                  <Box direction="vertical" width="100%">
                    <InputFieldWithLabel
                      required
                      autoFocus={!isMobile}
                      formField={store.codeField}
                      label={t('enter_code.code_input_placeholder')}
                      type="text"
                      dataHook={dataHooks.enterCode.codeInput}
                      onEnterPressed={submit}
                    />
                    <Box width="100%" align="left" className={s.resend}>
                      <ResendCode store={store} />
                    </Box>
                  </Box>
                  {captcha}
                </Box>
                {/* The divider brings its own vertical padding. */}
                <Box direction="vertical" gap={1} width="100%">
                  <ButtonWithLoader
                    dataHook={dataHooks.enterCode.verifyButton}
                    size={undefined}
                    type="submit"
                    skin="standard"
                    priority="primary"
                    onClick={submit}
                    // Keeps spinning after a verified code until the page is left.
                    showLoader={store.isCompleting}
                    disabled={store.isLoading && !store.isCompleting}
                    behaviour={BEHAVIORS.MULTI_CLICKS}
                  >
                    {submitLabel}
                  </ButtonWithLoader>
                  <AuthDivider
                    text={t('emailStep.divider')}
                    className={classNames({ [s.divider]: isVerticalLayout })}
                  />
                  <ButtonWithLoader
                    size={undefined}
                    priority="secondary"
                    className={s.outlinedButton}
                    disabled={store.isLoading}
                    onClick={store.switchToPassword}
                    dataHook={`${dataHookPrefix}.password`}
                  >
                    {passwordLabel}
                  </ButtonWithLoader>
                </Box>
              </Box>
              {children}
            </BodyContainer>
          </DialogFormContainer>
          <AuthPage.Policies>{belowCard}</AuthPage.Policies>
        </Box>
      </PageContextProvider>
    </GenericEventsBiLoggerProvider>
  );
});
